import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Gem, Crown, AlertCircle, RefreshCw } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { formatDate } from '../lib/utils';
import { SubscriptionPlan, GemPackage } from '../lib/ehPrices';

interface EhPaymentSuccessDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  purchaseOption: SubscriptionPlan | GemPackage | null;
  paymentCheckStatus?: '' | 'pending' | 'verified' | 'failed';
  subscriptionEndDate?: string;
  onRetryVerification?: () => void;
}

export function EhPaymentSuccessDialog({
  open,
  onOpenChange,
  purchaseOption,
  paymentCheckStatus = 'verified',
  subscriptionEndDate,
  onRetryVerification
}: EhPaymentSuccessDialogProps) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(open);
  
  useEffect(() => {
    setIsOpen(open);
  }, [open]);
  
  const handleOpenChange = (newOpen: boolean) => {
    setIsOpen(newOpen);
    onOpenChange(newOpen);
  };
  
  const handleGoToChat = () => {
    handleOpenChange(false);
    void navigate('/');
  };

  const isVerifying = paymentCheckStatus === 'pending' || paymentCheckStatus === '';
  const verificationFailed = paymentCheckStatus === 'failed';
  const isSubscription = purchaseOption && 'isSubscription' in purchaseOption && purchaseOption.isSubscription;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md bg-gradient-to-br from-zinc-900 to-zinc-950 border-zinc-800">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2 mb-2">
            {paymentCheckStatus === 'verified' && <Check className="h-5 w-5 text-green-400" />}
            {/* {isVerifying && <div className="animate-spin h-5 w-5 border-2 border-blue-400 border-t-transparent rounded-full" />} */}
            {verificationFailed && <AlertCircle className="h-5 w-5 text-amber-400" />}
            <div>Thank You For Your Purchase!</div>
          </DialogTitle>
          <DialogDescription className={`text-zinc-300 ${ paymentCheckStatus !== 'verified' ? 'hidden' : '' }`}>
            {isSubscription 
              ? 'Your subscription has been activated.'
              : 'Your gems have been added to your balance.'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4 flex flex-col items-center justify-center">
          {isVerifying && (
            <div className="flex flex-col items-center gap-2 py-2">
              <div className="animate-spin h-8 w-8 border-3 border-blue-400 border-t-transparent rounded-full" />
              <p className="text-white text-center">Verifying your payment...</p>
              <p className="text-sm text-zinc-300 text-center">It may take a few minutes to activate your purchase.</p>
            </div>
          )}
          
          {verificationFailed && (
            <div className="flex flex-col items-center gap-2 py-2">
              <AlertCircle className="h-12 w-12 text-amber-400" />
              <p className="text-white text-center">Payment verification pending</p>
              <p className="text-sm text-zinc-300 text-center mb-2">
                We're still working to verify your payment. Your purchase will be added once verified.
              </p>
              {onRetryVerification && (
                <Button
                  onClick={onRetryVerification}
                  className="bg-slate-600 hover:bg-slate-700"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  <span>Retry Verification</span>
                </Button>
              )}
            </div>
          )}
          
          {paymentCheckStatus === 'verified' && (
            isSubscription ? (
              <div className="flex flex-col items-center">
                <Crown className="h-12 w-12 text-amber-400 mb-2" />
                <p className="text-xl font-bold text-white">Subscription Activated</p>
                {subscriptionEndDate && (
                  <p className="text-sm text-zinc-300 mt-1">Valid until {formatDate(subscriptionEndDate)}</p>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <div className="flex items-center">
                  <Gem className="h-10 w-10 text-amber-400 mr-1" />
                  <span className="text-2xl font-bold text-white">{(purchaseOption as GemPackage)?.gems.toLocaleString()}</span>
                </div>
                <p className="text-sm text-zinc-300 mt-1">Gems added to your balance</p>
              </div>
            )
          )}
        </div>
        
        <DialogFooter className="flex flex-col sm:flex-row gap-4 sm:gap-2 mt-2">
          {paymentCheckStatus === 'verified' && (
            <Button
              type="button"
              className="bg-pink-700 hover:bg-pink-800 text-white"
              onClick={handleGoToChat}
            >
              Start Chatting
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
} 